import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, X, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

export default function AdminMenuItems() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [optionSets, setOptionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ categoryId: '', name: '', description: '', price: '', image: '', popular: false, isNew: false, available: true, isVeggie: false, isSpicy: false, isUpsell: false });
  const [selectedOptionSets, setSelectedOptionSets] = useState([]);
  const [openPricingSets, setOpenPricingSets] = useState({});
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const load = async () => {
    const [cats, items, sets] = await Promise.all([
      api.getCategories(),
      api.getMenuItems(),
      api.getOptionSets().catch(() => [])
    ]);
    setCategories(cats);
    setMenuItems(items);
    setOptionSets(sets);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAddForm = () => {
    setForm({ categoryId: '', name: '', description: '', price: '', image: '', popular: false, isNew: false, available: true, isVeggie: false, isSpicy: false, isUpsell: false });
    setSelectedOptionSets([]);
    setOpenPricingSets({});
    setEditing(null);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setForm({ 
      ...item, 
      price: item.price?.toString() || '', 
      isNew: item.isNew || false, 
      isVeggie: item.isVeggie || false, 
      isSpicy: item.isSpicy || false,
      isUpsell: item.isUpsell || false 
    });
    // Pre-select option sets that match item's options by name
    const matchedSets = (item.options || []).map(opt => ({
  ...opt,
  dependsOn: opt.dependsOn || null, // 👈 ADD THIS LINE
  choices: (opt.choices || []).map(choice => ({
    ...choice
  }))
}));

    setSelectedOptionSets(matchedSets);
    setOpenPricingSets({});
    setEditing(item.id);
    setShowForm(true);
  };

  const toggleOptionSet = (set) => {
  setSelectedOptionSets(prev => {
    const exists = prev.find(s => s.id === set.id);
    if (exists) {
      return prev.filter(s => s.id !== set.id);
    }
    return [
    ...prev,
    {
    ...set,
    dependsOn: null, // 👈 ADD THIS
    choices: set.choices.map(choice => ({
      ...choice
    }))
  }
  ];
  });
  };

  const updateChoicePrice = (setId, choiceId, value) => {
  setSelectedOptionSets(prev =>
    prev.map(set => {
      if (set.id !== setId) return set;

      return {
        ...set,
        choices: set.choices.map(choice =>
          choice.id === choiceId
            ? { ...choice, price: parseFloat(value) || 0 }
            : choice
        )
      };
    })
  );
};

const updateDependency = (setId, dependsOn) => {
  setSelectedOptionSets(prev =>
    prev.map(set =>
      set.id === setId
        ? {
            ...set,
            dependsOn: dependsOn.optionId ? dependsOn : null
          }
        : set
    )
  );
};

  const moveOptionSet = (index, direction) => {
  const newArr = [...selectedOptionSets];
  const target = index + direction;

  if (target < 0 || target >= newArr.length) return;

  [newArr[index], newArr[target]] = [newArr[target], newArr[index]];
  setSelectedOptionSets(newArr);
  };

  const handleSave = async () => {
    if (!form.name || !form.categoryId || !form.price) {
      setError('Name, category and price are required');
      return;
    }
    setError('');
    // Build options from selected option sets
    const selectedSets = selectedOptionSets;
    const options = selectedSets.map(s => ({
    id: s.id,
    name: s.name,
    required: s.required,
    multiSelect: s.multiSelect,
    dependsOn: s.dependsOn?.optionId && s.dependsOn.optionId !== '__pending__' ? s.dependsOn : null,
    choices: s.choices
  }));

  console.log("SAVING OPTIONS:", options);

    const payload = { ...form, price: parseFloat(form.price), options };
    try {
      if (editing) {
        await api.updateMenuItem(editing, payload);
      } else {
        await api.createMenuItem(payload);
      }
      await load();
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await api.deleteMenuItem(id);
    load();
  };

  const filteredItems = menuItems.filter(item => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || item.name?.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query);
    const matchesCategory = categoryFilter === 'all' || item.categoryId === categoryFilter;
    const matchesAvailability = availabilityFilter === 'all' || (availabilityFilter === 'available' ? item.available : !item.available);
    return matchesSearch && matchesCategory && matchesAvailability;
  });
  const knownCategoryIds = new Set(categories.map(category => String(category.id)));
  const filteredCategorySections = categories
    .map(category => ({
      ...category,
      items: filteredItems.filter(item => String(item.categoryId) === String(category.id))
    }))
    .filter(category => category.items.length > 0);
  const uncategorizedItems = filteredItems.filter(item => !knownCategoryIds.has(String(item.categoryId)));
  if (uncategorizedItems.length > 0) {
    filteredCategorySections.push({ id: '__uncategorized__', name: 'Uncategorised', items: uncategorizedItems });
  }
  const categoryPages = filteredCategorySections.reduce((pages, category) => {
    const currentPage = pages[pages.length - 1];
    const currentCount = currentPage.reduce((total, section) => total + section.items.length, 0);
    if (currentPage.length > 0 && currentCount + category.items.length > pageSize) {
      pages.push([category]);
    } else {
      currentPage.push(category);
    }
    return pages;
  }, [[]]);
  const totalPages = Math.max(1, categoryPages.length);
  const effectivePage = Math.min(page, totalPages);
  const categorySections = categoryPages[effectivePage - 1] || [];
  const changePage = (nextPage) => {
    setPage(nextPage);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  useEffect(() => { setPage(1); }, [searchQuery, categoryFilter, availabilityFilter]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div><h2 className="text-lg font-bold text-gray-900">Menu Items</h2><p className="text-gray-500 text-sm">{menuItems.length} items</p></div>
        <div className="admin-toolbar">
          <div className="admin-search-field">
            <Search className="w-4 h-4" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search menu items..." />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} aria-label="Filter by category">
            <option value="all">All categories</option>
            {categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
          <select value={availabilityFilter} onChange={e => setAvailabilityFilter(e.target.value)} aria-label="Filter by availability">
            <option value="all">Any availability</option>
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
          </select>
          <span className="admin-result-count">{filteredItems.length} results</span>
        </div>
        <button onClick={openAddForm} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div></AdminTopbarPortal>

      {showForm && (
        <div className="admin-drawer-backdrop" onMouseDown={() => { setShowForm(false); setEditing(null); }}>
        <div className="admin-drawer" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={editing ? 'Edit menu item' : 'New menu item'}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">{editing ? 'Edit Menu Item' : 'New Menu Item'}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
          </div>
          <div className="admin-form-grid grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Item Name *</label>
              <input type="text" placeholder="e.g. Crispy Chicken Wrap" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Category *</label>
              <select value={form.categoryId} onChange={e => setForm(p => ({ ...p, categoryId: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white">
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Price (£) *</label>
              <input type="number" placeholder="0.00" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Description</label>
              <textarea placeholder="Describe the item..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={2} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Image URL</label>
              <input type="url" placeholder="https://..." value={form.image} onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div className="admin-flag-grid sm:col-span-2">
              <label className="admin-flag-option">
                <input type="checkbox" checked={form.popular} onChange={e => setForm(p => ({ ...p, popular: e.target.checked }))} className="w-4 h-4 rounded accent-amber-600" />
                <span className="text-sm text-gray-700">Popular</span>
              </label>
              <label className="admin-flag-option">
                <input type="checkbox" checked={form.isNew} onChange={e => setForm(p => ({ ...p, isNew: e.target.checked }))} className="w-4 h-4 rounded accent-amber-600" />
                <span className="text-sm text-gray-700">New</span>
              </label>
              <label className="admin-flag-option">
                <input type="checkbox" checked={form.isVeggie} onChange={e => setForm(p => ({ ...p, isVeggie: e.target.checked }))} className="w-4 h-4 rounded accent-emerald-600" />
                <span className="text-sm text-gray-700">Veggie</span>
              </label>
              <label className="admin-flag-option">
                <input type="checkbox" checked={form.isSpicy} onChange={e => setForm(p => ({ ...p, isSpicy: e.target.checked }))} className="w-4 h-4 rounded accent-red-600" />
                <span className="text-sm text-gray-700">Spicy</span>
              </label>
              <label className="admin-flag-option">
                <input type="checkbox" checked={form.available} onChange={e => setForm(p => ({ ...p, available: e.target.checked }))} className="w-4 h-4 rounded accent-amber-600" />
                <span className="text-sm text-gray-700">Available</span>
              </label>
              <label className="admin-flag-option">
              <input
                type="checkbox"
                checked={form.isUpsell}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    isUpsell: e.target.checked
                  }))
                }
                className="w-4 h-4 rounded accent-blue-600"
              />
              <span className="text-sm text-gray-700">
                Upsell Item
              </span>
            </label>
            </div>
          </div>

          {/* Option Sets Selector */}
<div className="mt-4">
  <label className="text-xs font-semibold text-gray-700 block mb-2">
    Item Options
    <span className="ml-1 font-normal text-gray-400">
      (select from saved option sets)
    </span>
  </label>

  {optionSets.length === 0 ? (
    <p className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3">
      No option sets yet. Create them in the <strong>Option Sets</strong> tab.
    </p>
  ) : (
    <>
      {/* ✅ SELECTED (ORDERED) */}
      {selectedOptionSets.length > 0 && (
        <div className="admin-selected-option-sets mb-4 space-y-2">
          <p className="admin-option-picker-heading">
            <strong>Selected option sets</strong><span>Drag order is reflected on the customer menu</span>
          </p>

          {selectedOptionSets.map((set, index) => (
            <div
              key={set.id}
              className="admin-option-set-card flex items-start justify-between p-4 border border-gray-200 rounded-xl bg-white hover:border-gray-300 transition"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">{set.name}</p>
                <p className="text-xs text-gray-500">
                  {set.choices?.length} choices ·{" "}
                  {set.required ? "Required" : "Optional"}
                </p>
                <button
                  type="button"
                  className="admin-pricing-toggle"
                  aria-expanded={Boolean(openPricingSets[set.id])}
                  onClick={() => setOpenPricingSets(current => ({ ...current, [set.id]: !current[set.id] }))}
                >
                  <span>Custom option pricing</span>
                  <ChevronDown className={openPricingSets[set.id] ? 'is-open' : ''} />
                </button>

                {openPricingSets[set.id] && (
                  <div className="admin-custom-pricing-panel">
                    {set.choices.map(choice => (
                      <div key={choice.id} className="admin-option-choice flex items-center justify-between text-xs">
                        <span className="text-gray-600">{choice.name}</span>
                        <input
                          type="number"
                          step="0.01"
                          value={choice.price}
                          onChange={(e) => updateChoicePrice(set.id, choice.id, e.target.value)}
                          className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-right text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div className="admin-option-dependency mt-3 flex items-center gap-2 flex-wrap">
  <p className="text-xs text-gray-500 mb-1">Show this when:</p>

  <select
    value={set.dependsOn ? 'conditional' : 'always'}
    onChange={(e) => {
      if (e.target.value === 'always') {
        updateDependency(set.id, { optionId: '', choiceId: '' });
        return;
      }
      const firstAvailableSet = selectedOptionSets.find(candidate => candidate.id !== set.id);
      updateDependency(set.id, {
        optionId: firstAvailableSet?.id || '__pending__',
        choiceId: ''
      });
    }}
    className="admin-condition-mode-select border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-amber-500"
  >
    <option value="always">Always show</option>
    <option value="conditional">Show conditionally</option>
  </select>

  {set.dependsOn && (
    <>
    <select
      value={set.dependsOn.optionId === '__pending__' ? '' : set.dependsOn.optionId}
      onChange={(e) => updateDependency(set.id, { optionId: e.target.value || '__pending__', choiceId: '' })}
      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-amber-500 min-w-[120px]"
    >
      <option value="">Select option set</option>
      {selectedOptionSets
        .filter(candidate => candidate.id !== set.id)
        .map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
    </select>
    <select
      value={set.dependsOn?.choiceId || ''}
      disabled={!set.dependsOn?.optionId || set.dependsOn.optionId === '__pending__'}
      onChange={(e) =>
        updateDependency(set.id, {
          ...set.dependsOn,
          choiceId: e.target.value
        })
      }
        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-amber-500 min-w-[100px]"
    >
      <option value="">Select choice</option>

      {selectedOptionSets
        .find(s => s.id === set.dependsOn.optionId)
        ?.choices.map(c => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
    </select>
    {selectedOptionSets.filter(candidate => candidate.id !== set.id).length === 0 && (
      <span className="admin-dependency-hint">Add another option set to choose a condition.</span>
    )}
    </>
  )}
</div>

              </div>

              

              <div className="admin-option-order-controls flex gap-1 items-center">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => moveOptionSet(index, -1)}
                  className="admin-order-button text-xs bg-white border disabled:opacity-30"
                  aria-label={`Move ${set.name} up`}
                >
                  ↑
                </button>

                <button
                  type="button"
                  disabled={index === selectedOptionSets.length - 1}
                  onClick={() => moveOptionSet(index, 1)}
                  className="admin-order-button text-xs bg-white border disabled:opacity-30"
                  aria-label={`Move ${set.name} down`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => toggleOptionSet(set)}
                  className="admin-order-button text-xs text-red-500 hover:bg-red-50"
                  aria-label={`Remove ${set.name}`}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ AVAILABLE OPTIONS */}
      <div className="admin-add-option-sets-group">
      <p className="admin-option-picker-heading"><strong>Add option sets</strong><span>Choose another saved set</span></p>
      <div className="admin-available-option-sets grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
        {optionSets
          .filter(set => !selectedOptionSets.find(s => s.id === set.id))
          .map(set => (
            <button
              key={set.id}
              type="button"
              onClick={() => toggleOptionSet(set)}
              className="flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150 border-gray-200 hover:border-gray-300 bg-white"
            >
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {set.name}
                </p>
                <p className="text-xs text-gray-400">
                  {set.choices?.length} choices ·{" "}
                  {set.required ? "Required" : "Optional"} ·{" "}
                  {set.multiSelect ? "Multi-select" : "Single"}
                </p>
              </div>
            </button>
          ))}
      </div>
      </div>
    </>
  )}
</div>

          {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
          <div className="admin-drawer-footer">
            <button onClick={handleSave} className="bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">{editing ? 'Update Item' : 'Add Item'}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-2.5 text-gray-600 text-sm font-semibold hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
          </div>
        </div>
        </div>
      )}

      {/* Items list by category */}
      <div className="space-y-3">
        {categorySections.map(cat => (
            <div key={cat.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h3 className="font-semibold text-gray-700 text-sm">{cat.name} <span className="text-gray-400 font-normal">({cat.items.length})</span></h3>
              </div>
              <div className="divide-y divide-gray-100">
                {cat.items.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors">
                    <img src={item.image || '/48.svg'} alt={item.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0 bg-gray-100" onError={e => { e.target.src='/48.svg'; }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-1.5">
                        <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                        <span className={`admin-menu-item-badge ${item.available ? 'is-available' : 'is-unavailable'}`}>{item.available ? 'Available' : 'Unavailable'}</span>
                        {item.popular && <span className="admin-menu-item-badge is-popular">Popular</span>}
                        {item.isNew && <span className="admin-menu-item-badge is-new">New</span>}
                        {item.isVeggie && <span className="admin-menu-item-badge is-veggie">Veggie</span>}
                        {item.isSpicy && <span className="admin-menu-item-badge is-spicy">Spicy</span>}
                        {item.isUpsell && <span className="admin-menu-item-badge is-upsell">Upsell</span>}
                        {item.options?.length > 0 && <span className="admin-menu-item-badge is-options">{item.options.length} options</span>}
                      </div>
                      <p className="text-gray-500 text-xs line-clamp-1 mt-0.5">{item.description}</p>
                    </div>
                    <span className="font-bold text-gray-900 text-sm">£{item.price?.toFixed(2)}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditForm(item)} className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50 transition-colors"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        ))}
      </div>
      {filteredItems.length === 0 && <div className="admin-empty-state">No menu items match those filters.</div>}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={effectivePage === 1} onClick={() => changePage(effectivePage - 1)}>Previous</button>
          <span>Page {effectivePage} of {totalPages}</span>
          <button disabled={effectivePage === totalPages} onClick={() => changePage(effectivePage + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
